import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  Edit as EditIcon,
  Event as EventIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Checkroom as CheckroomIcon
} from '@mui/icons-material';
import { AdminUserCreator } from './AdminUserCreator';
import { CompatibilityService } from '../services/compatibility';

interface Guest {
  id: string;
  email: string;
  full_name: string;
  phone_number: string;
  oauth_provider: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface GuestEvent {
  id: string;
  name: string;
  event_date: string;
  start_time: string;
  is_mandatory: boolean;
  registration_status: string | null;
  registered_at: string | null;
}

export const GuestManagement: React.FC = () => {
  const [guests, setGuests] = useState<Guest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Initialize compatibility service
  const compatibilityService = CompatibilityService.getInstance({
    useUnifiedApi: false, // Start with legacy for backward compatibility
    legacyFormat: true,
    featureFlags: {
      'pin-authentication': true,
      'unified-guest-management': false // Can be enabled via feature flag
    }
  });
  
  // Edit guest dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingGuest, setEditingGuest] = useState<Guest | null>(null);
  const [editForm, setEditForm] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    status: ''
  });
  
  // Guest events dialog
  const [eventsDialogOpen, setEventsDialogOpen] = useState(false);
  const [guestEvents, setGuestEvents] = useState<GuestEvent[]>([]);
  const [selectedGuestId, setSelectedGuestId] = useState<string | null>(null);
  const [eventsLoading, setEventsLoading] = useState(false);
  
  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [guestToDelete, setGuestToDelete] = useState<Guest | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Clothing assignment dialog
  const [clothingDialogOpen, setClothingDialogOpen] = useState(false);
  const [clothingAssignments, setClothingAssignments] = useState<any[]>([]);
  const [clothingLoading, setClothingLoading] = useState(false);
  const [selectedGuestForClothing, setSelectedGuestForClothing] = useState<Guest | null>(null);
  const [clothingError, setClothingError] = useState<string | null>(null);
  
  // Add/Edit clothing assignment form
  const [clothingFormOpen, setClothingFormOpen] = useState(false);
  const [editingClothingId, setEditingClothingId] = useState<string | null>(null);
  const [clothingForm, setClothingForm] = useState({
    eventId: '',
    itemType: '',
    size: '',
    color: '',
    pickupInstructions: ''
  });
  const [availableEvents, setAvailableEvents] = useState<any[]>([]);
  
  // Invalid guests cleanup
  const [invalidGuestsDialogOpen, setInvalidGuestsDialogOpen] = useState(false);
  const [invalidGuests, setInvalidGuests] = useState<any[]>([]);
  const [invalidGuestsLoading, setInvalidGuestsLoading] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  
  // Clothing item types and their size options
  const clothingItemTypes = [
    { value: 'T-Shirt', label: 'T-Shirt' },
    { value: 'Golf Polo Shirt', label: 'Golf Polo Shirt' },
    { value: 'African Style Cargo Pants', label: 'African Style Cargo Pants' },
    { value: 'Hat', label: 'Hat' }
  ];
  
  const getAvailableSizes = (itemType: string) => {
    if (itemType === 'African Style Cargo Pants') {
      return [
        'Regular Length',
        'Regular Length & Extra Wide Waist',
        'Long',
        'Extra Long',
        'Extra Long & Extra Wide Waist'
      ];
    }
    if (itemType === 'Hat') {
      return ['One Size'];
    }
    // Standard sizes for T-Shirts and Golf Polo Shirts
    return ['XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', '3XL'];
  };

  useEffect(() => {
    loadGuests();
  }, []);

  const validateGuestRecord = (guest: any): guest is Guest => {
    // Check if guest has required fields and they're not empty/null
    if (!guest || typeof guest !== 'object') return false;
    if (!guest.id || typeof guest.id !== 'string' || guest.id.trim() === '') return false;
    if (!guest.full_name || typeof guest.full_name !== 'string' || guest.full_name.trim() === '') return false;
    if (!guest.email || typeof guest.email !== 'string' || guest.email.trim() === '') return false;
    if (!guest.created_at || guest.created_at === 'Invalid date') return false;
    
    // Validate created_at is a valid date
    const createdDate = new Date(guest.created_at);
    if (isNaN(createdDate.getTime())) return false;
    
    return true;
  };

  const loadGuests = async () => {
    try {
      const isDemoMode = (window as any).DEMO_MODE;
      
      if (isDemoMode) {
        const { mockApiCall } = await import('../services/demoData');
        const data = await mockApiCall('/api/admin/guests');
        const rawGuests = data.data.guests || [];
        const validGuests = rawGuests.filter(validateGuestRecord);
        setGuests(validGuests);
        setLoading(false);
        return;
      }

      // Use compatibility service for backward compatible guest loading
      const filters = {
        status: statusFilter === 'all' ? undefined : statusFilter,
        search: searchTerm || undefined
      };

      const data = await compatibilityService.getGuests(filters);
      
      if (data.success) {
        // Handle both old format (data.data.guests) and new format (data.data)
        const rawGuestsList = data.data.guests || data.data;
        const guestsList = Array.isArray(rawGuestsList) ? rawGuestsList : [];
        
        // Filter out invalid/incomplete guest records
        const validGuests = guestsList.filter(validateGuestRecord);
        
        console.log(`Loaded ${guestsList.length} raw guests, ${validGuests.length} valid guests`);
        if (guestsList.length !== validGuests.length) {
          console.warn(`Filtered out ${guestsList.length - validGuests.length} invalid guest records`);
        }
        
        setGuests(validGuests);
      } else {
        throw new Error(data.error || 'Failed to load guests');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load guests');
      
      // Fallback to legacy API if unified API fails
      try {
        console.log('Falling back to legacy API...');
        const { ApiService } = await import('../services/api');
        const fallbackData = await ApiService.getGuests();
        
        if (fallbackData.success) {
          const rawGuestsList = fallbackData.data.guests || fallbackData.data;
          const guestsList = Array.isArray(rawGuestsList) ? rawGuestsList : [];
          const validGuests = guestsList.filter(validateGuestRecord);
          setGuests(validGuests);
          setError(null); // Clear error if fallback succeeds
        }
      } catch (fallbackErr) {
        console.error('Fallback also failed:', fallbackErr);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditGuest = (guest: Guest) => {
    setEditingGuest(guest);
    setEditForm({
      fullName: guest.full_name,
      email: guest.email,
      phoneNumber: guest.phone_number || '',
      status: guest.status
    });
    setEditDialogOpen(true);
  };

  const handleSaveGuest = async () => {
    if (!editingGuest) return;

    try {
      const { ApiService } = await import('../services/api');
      const result = await ApiService.updateGuest(editingGuest.id, {
        fullName: editForm.fullName,
        email: editForm.email,
        phoneNumber: editForm.phoneNumber,
        status: editForm.status
      });

      if (result.success) {
        setEditDialogOpen(false);
        setEditingGuest(null);
        loadGuests();
      } else {
        throw new Error(result.error || 'Failed to update guest');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update guest');
    }
  };

  const handleDeleteGuest = (guest: Guest) => {
    setGuestToDelete(guest);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteGuest = async () => {
    if (!guestToDelete) return;

    setDeleteLoading(true);
    try {
      const { ApiService } = await import('../services/api');
      const result = await ApiService.deleteGuest(guestToDelete.id);
      
      if (result.success) {
        // Immediately remove the guest from local state for instant UI update
        setGuests(prevGuests => prevGuests.filter(guest => guest.id !== guestToDelete.id));
        
        setDeleteDialogOpen(false);
        setGuestToDelete(null);
        
        // Show detailed success message
        const details = result.data;
        let successMessage = 'Guest deleted successfully!';
        
        if (details) {
          const statusItems = [];
          if (details.cognitoDeleted) statusItems.push('✅ Cognito account deleted');
          if (details.emailSent) statusItems.push('✅ Notification email sent');
          if (details.auditLogged) statusItems.push('✅ Action logged for audit');
          
          if (statusItems.length > 0) {
            successMessage += '\n\n' + statusItems.join('\n');
          }
        }
        
        console.log('Deletion completed:', successMessage);
        
        // Also refresh from server to ensure consistency
        setTimeout(() => loadGuests(), 1000);
      } else {
        throw new Error(result.error || 'Failed to delete guest');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete guest');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleViewEvents = async (guestId: string) => {
    setEventsLoading(true);
    try {
      const { ApiService } = await import('../services/api');
      const result = await ApiService.getGuestEvents(guestId);
      console.log('getGuestEvents result:', result);
      
      if (result.success) {
        console.log('Setting guest events:', result.data.events);
        setGuestEvents(result.data.events);
        setSelectedGuestId(guestId);
        setEventsDialogOpen(true);
      } else {
        throw new Error(result.error || 'Failed to load guest events');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load guest events');
    } finally {
      setEventsLoading(false);
    }
  };

  const handleToggleEventRegistration = async (eventId: string, isRegistered: boolean) => {
    if (!selectedGuestId) return;

    console.log('Toggle event registration:', { eventId, isRegistered, selectedGuestId });

    try {
      const { ApiService } = await import('../services/api');
      const action = isRegistered ? 'unregister' : 'register';
      console.log('Calling API with action:', action);
      const result = await ApiService.updateGuestEventRegistration(selectedGuestId, eventId, action);
      console.log('API result:', result);
      
      if (result.success) {
        // Refresh events
        console.log('Refreshing events list...');
        await handleViewEvents(selectedGuestId);
        console.log('Events refreshed');
      } else {
        throw new Error(result.error || 'Failed to update event registration');
      }
    } catch (err) {
      console.error('Error toggling event:', err);
      setError(err instanceof Error ? err.message : 'Failed to update event registration');
    }
  };

  const handleViewClothing = async (guest: Guest) => {
    setSelectedGuestForClothing(guest);
    setClothingLoading(true);
    setClothingError(null);
    
    try {
      const { ApiService } = await import('../services/api');
      
      // Load clothing assignments
      const clothingResult = await ApiService.getGuestClothingAssignments(guest.id);
      if (clothingResult.success) {
        setClothingAssignments(clothingResult.data.assignments || []);
      } else {
        throw new Error(clothingResult.error || 'Failed to load clothing assignments');
      }
      
      // Load available events for the form
      const eventsResult = await ApiService.getEvents();
      if (eventsResult.success) {
        setAvailableEvents(eventsResult.data.events || []);
      }
      
      setClothingDialogOpen(true);
    } catch (err) {
      setClothingError(err instanceof Error ? err.message : 'Failed to load clothing assignments');
    } finally {
      setClothingLoading(false);
    }
  };

  const handleAddClothing = () => {
    setEditingClothingId(null);
    setClothingForm({
      eventId: '',
      itemType: '',
      size: '',
      color: '',
      pickupInstructions: ''
    });
    setClothingFormOpen(true);
  };

  const handleEditClothing = (assignment: any) => {
    setEditingClothingId(assignment.id);
    setClothingForm({
      eventId: assignment.eventId || '',
      itemType: assignment.itemType || '',
      size: assignment.size || '',
      color: assignment.color || '',
      pickupInstructions: assignment.pickupInstructions || ''
    });
    setClothingFormOpen(true);
  };

  const handleSaveClothing = async () => {
    if (!selectedGuestForClothing) return;
    
    setClothingLoading(true);
    setClothingError(null);
    
    try {
      const { ApiService } = await import('../services/api');
      
      if (editingClothingId) {
        // Update existing assignment
        console.log('Updating clothing assignment:', editingClothingId, clothingForm);
        const result = await ApiService.updateClothingAssignment(editingClothingId, {
          size: clothingForm.size,
          itemType: clothingForm.itemType,
          color: clothingForm.color || undefined,
          pickupInstructions: clothingForm.pickupInstructions || undefined
        });
        
        console.log('Update result:', result);
        
        if (!result.success) {
          throw new Error(result.error || 'Failed to update clothing assignment');
        }
      } else {
        // Create new assignment
        console.log('Creating clothing assignment for guest:', selectedGuestForClothing.id, clothingForm);
        const result = await ApiService.addClothingAssignment(selectedGuestForClothing.id, {
          eventId: clothingForm.eventId,
          itemType: clothingForm.itemType,
          size: clothingForm.size,
          color: clothingForm.color || undefined,
          pickupInstructions: clothingForm.pickupInstructions || undefined
        });
        
        console.log('Create result:', result);
        
        if (!result.success) {
          throw new Error(result.error || 'Failed to create clothing assignment');
        }
      }
      
      // Refresh clothing assignments
      console.log('Refreshing clothing assignments...');
      const clothingResult = await ApiService.getGuestClothingAssignments(selectedGuestForClothing.id);
      console.log('Refresh result:', clothingResult);
      
      if (clothingResult.success) {
        setClothingAssignments(clothingResult.data.assignments || []);
      }
      
      setClothingFormOpen(false);
      
      // Reset form
      setClothingForm({
        eventId: '',
        itemType: '',
        size: '',
        color: '',
        pickupInstructions: ''
      });
    } catch (err) {
      console.error('Error saving clothing assignment:', err);
      setClothingError(err instanceof Error ? err.message : 'Failed to save clothing assignment');
    } finally {
      setClothingLoading(false);
    }
  };

  const handleDeleteClothing = async (assignmentId: string) => {
    if (!selectedGuestForClothing) return;
    
    if (!window.confirm('Are you sure you want to delete this clothing assignment?')) {
      return;
    }
    
    setClothingLoading(true);
    setClothingError(null);
    
    try {
      const { ApiService } = await import('../services/api');
      const result = await ApiService.removeClothingAssignment(assignmentId);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete clothing assignment');
      }
      
      // Refresh clothing assignments
      const clothingResult = await ApiService.getGuestClothingAssignments(selectedGuestForClothing.id);
      if (clothingResult.success) {
        setClothingAssignments(clothingResult.data.assignments || []);
      }
    } catch (err) {
      setClothingError(err instanceof Error ? err.message : 'Failed to delete clothing assignment');
    } finally {
      setClothingLoading(false);
    }
  };

  const handleViewInvalidGuests = async () => {
    setInvalidGuestsLoading(true);
    setInvalidGuestsDialogOpen(true);
    
    try {
      const { ApiService } = await import('../services/api');
      const result = await ApiService.get('/admin/guests/invalid');
      
      if (result.success) {
        setInvalidGuests(result.data.invalidGuests || []);
      } else {
        throw new Error(result.error || 'Failed to load invalid guests');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load invalid guests');
    } finally {
      setInvalidGuestsLoading(false);
    }
  };

  const handleCleanupInvalidGuests = async () => {
    if (!window.confirm(`Are you sure you want to delete ${invalidGuests.length} invalid guest record(s)? This action cannot be undone.`)) {
      return;
    }
    
    setCleanupLoading(true);
    
    try {
      const { ApiService } = await import('../services/api');
      const result = await ApiService.delete('/admin/guests/cleanup-invalid');
      
      if (result.success) {
        alert(`Successfully deleted ${result.data.deletedCount} invalid guest(s)`);
        setInvalidGuestsDialogOpen(false);
        // Refresh the main guest list
        loadGuests();
      } else {
        throw new Error(result.error || 'Failed to cleanup invalid guests');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cleanup invalid guests');
    } finally {
      setCleanupLoading(false);
    }
  };

  const filteredGuests = guests.filter(guest => {
    const matchesSearch = !searchTerm || 
      guest.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      guest.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || guest.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5">
          Guest Management
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            color="warning"
            onClick={handleViewInvalidGuests}
            disabled={invalidGuestsLoading}
          >
            View Invalid Guests
          </Button>
          <AdminUserCreator onUserCreated={loadGuests} />
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Search guests"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <Button
            variant="outlined"
            onClick={loadGuests}
            fullWidth
            sx={{ height: '56px' }}
          >
            Refresh
          </Button>
        </Grid>
      </Grid>

      {/* Guests Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Provider</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Joined</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredGuests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    {loading ? 'Loading guests...' : 'No guests found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredGuests.map((guest) => (
                <TableRow key={guest.id}>
                  <TableCell>{guest.full_name || '-'}</TableCell>
                  <TableCell>{guest.email || '-'}</TableCell>
                  <TableCell>{guest.phone_number || '-'}</TableCell>
                  <TableCell>
                    <Chip 
                      label={guest.oauth_provider || 'cognito'} 
                      size="small" 
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={guest.status || 'active'} 
                      size="small"
                      color={(guest.status || 'active') === 'active' ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {guest.created_at ? new Date(guest.created_at).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleEditGuest(guest)}
                      title="Edit guest"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleViewEvents(guest.id)}
                      title="Manage events"
                      disabled={eventsLoading}
                    >
                      <EventIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleViewClothing(guest)}
                      title="Manage clothing"
                      disabled={clothingLoading}
                    >
                      <CheckroomIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteGuest(guest)}
                      title="Delete guest"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Guest Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Guest</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Full Name"
              value={editForm.fullName}
              onChange={(e) => setEditForm({ ...editForm, fullName: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Email"
              value={editForm.email}
              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Phone Number"
              value={editForm.phoneNumber}
              onChange={(e) => setEditForm({ ...editForm, phoneNumber: e.target.value })}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={editForm.status}
                label="Status"
                onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveGuest} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Guest Events Dialog */}
      <Dialog open={eventsDialogOpen} onClose={() => setEventsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Manage Guest Events</DialogTitle>
        <DialogContent>
          {eventsLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, px: 2 }}>
                Showing all {guestEvents.length} event(s) - Check/uncheck to register/unregister guest
              </Typography>
              <List>
                {guestEvents.map((event) => (
                  <ListItem key={event.id}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!event.registration_status}
                          onChange={() => handleToggleEventRegistration(event.id, !!event.registration_status)}
                          disabled={event.is_mandatory}
                        />
                      }
                      label=""
                    />
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          {event.name}
                          {event.is_mandatory && (
                            <Chip label="Mandatory" size="small" color="primary" />
                          )}
                          {event.registration_status && (
                            <Chip label="Registered" size="small" color="success" />
                          )}
                        </Box>
                      }
                      secondary={`${new Date(event.event_date + 'T00:00:00').toLocaleDateString()} at ${event.start_time}`}
                    />
                  </ListItem>
                ))}
                {guestEvents.length === 0 && (
                  <Box textAlign="center" py={4}>
                    <Typography variant="body2" color="text.secondary">
                      No events available
                    </Typography>
                  </Box>
                )}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEventsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Guest</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete this guest? This action cannot be undone.
          </Typography>
          {guestToDelete && (
            <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="subtitle2">Guest Details:</Typography>
              <Typography variant="body2">Name: {guestToDelete.full_name}</Typography>
              <Typography variant="body2">Email: {guestToDelete.email}</Typography>
            </Box>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              This will also delete the user's Cognito account if it exists.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button 
            onClick={confirmDeleteGuest} 
            variant="contained" 
            color="error"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleteLoading ? 'Deleting...' : 'Delete Guest'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clothing Assignment Dialog */}
      <Dialog open={clothingDialogOpen} onClose={() => setClothingDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <CheckroomIcon />
              <span>Manage Clothing Assignments</span>
            </Box>
            {selectedGuestForClothing && (
              <Typography variant="subtitle2" color="text.secondary">
                {selectedGuestForClothing.full_name}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {clothingError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setClothingError(null)}>
              {clothingError}
            </Alert>
          )}
          
          {clothingLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<CheckroomIcon />}
                  onClick={handleAddClothing}
                  disabled={clothingLoading}
                >
                  Add Clothing Assignment
                </Button>
              </Box>
              
              {clothingAssignments.length === 0 ? (
                <Alert severity="info">
                  No clothing assignments found. Click "Add Clothing Assignment" to create one.
                </Alert>
              ) : (
                <List>
                  {clothingAssignments.map((assignment) => (
                    <ListItem
                      key={assignment.id}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 1
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="subtitle1">{assignment.itemType}</Typography>
                            <Chip label={`Size: ${assignment.size}`} size="small" />
                            {assignment.color && (
                              <Chip label={assignment.color} size="small" variant="outlined" />
                            )}
                            <Chip 
                              label={assignment.status || 'assigned'} 
                              size="small" 
                              color="success"
                            />
                          </Box>
                        }
                        secondary={
                          <>
                            {assignment.eventName && (
                              <Typography variant="body2" color="text.secondary">
                                Event: {assignment.eventName}
                              </Typography>
                            )}
                            {assignment.pickupInstructions && (
                              <Typography variant="body2" color="text.secondary">
                                Pickup: {assignment.pickupInstructions}
                              </Typography>
                            )}
                            {assignment.assignedAt && (
                              <Typography variant="caption" color="text.secondary">
                                Assigned: {new Date(assignment.assignedAt).toLocaleDateString()}
                              </Typography>
                            )}
                          </>
                        }
                      />
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => handleEditClothing(assignment)}
                          title="Edit assignment"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClothing(assignment.id)}
                          title="Delete assignment"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClothingDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Clothing Assignment Form Dialog */}
      <Dialog open={clothingFormOpen} onClose={() => setClothingFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingClothingId ? 'Edit Clothing Assignment' : 'Add Clothing Assignment'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {!editingClothingId && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Event</InputLabel>
                <Select
                  value={clothingForm.eventId}
                  label="Event"
                  onChange={(e) => setClothingForm({ ...clothingForm, eventId: e.target.value })}
                >
                  {availableEvents.map((event) => (
                    <MenuItem key={event.id} value={event.id}>
                      {event.name} - {(() => {
                        const dateStr = event.eventDate || event.event_date;
                        const [year, month, day] = dateStr.split('-').map(Number);
                        return new Date(year, month - 1, day).toLocaleDateString();
                      })()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Item Type</InputLabel>
              <Select
                value={clothingForm.itemType}
                label="Item Type"
                onChange={(e) => {
                  setClothingForm({ 
                    ...clothingForm, 
                    itemType: e.target.value,
                    size: '' // Reset size when item type changes
                  });
                }}
              >
                {clothingItemTypes.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth sx={{ mb: 2 }} disabled={!clothingForm.itemType}>
              <InputLabel>Size</InputLabel>
              <Select
                value={clothingForm.size}
                label="Size"
                onChange={(e) => setClothingForm({ ...clothingForm, size: e.target.value })}
              >
                {clothingForm.itemType && getAvailableSizes(clothingForm.itemType).map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="Color (Optional)"
              value={clothingForm.color}
              onChange={(e) => setClothingForm({ ...clothingForm, color: e.target.value })}
              sx={{ mb: 2 }}
              placeholder="e.g., Navy Blue, White, Black"
            />
            
            <TextField
              fullWidth
              label="Pickup Instructions (Optional)"
              value={clothingForm.pickupInstructions}
              onChange={(e) => setClothingForm({ ...clothingForm, pickupInstructions: e.target.value })}
              multiline
              rows={2}
              placeholder="e.g., Available at front desk"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClothingFormOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSaveClothing} 
            variant="contained"
            disabled={!clothingForm.itemType || !clothingForm.size || (!editingClothingId && !clothingForm.eventId)}
          >
            {editingClothingId ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invalid Guests Dialog */}
      <Dialog open={invalidGuestsDialogOpen} onClose={() => setInvalidGuestsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <span>Invalid Guest Records</span>
            {invalidGuests.length > 0 && (
              <Chip label={`${invalidGuests.length} invalid`} color="warning" size="small" />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {invalidGuestsLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : invalidGuests.length === 0 ? (
            <Alert severity="success">
              No invalid guest records found. All guests in the database are valid!
            </Alert>
          ) : (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                These guest records have missing or invalid data and are not displayed in the Guest Management view.
                You can safely delete them to clean up your database and sync the guest count.
              </Alert>
              
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Issues</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invalidGuests.map((guest, index) => (
                      <TableRow key={guest.id || index}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {guest.id || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>{guest.full_name || 'N/A'}</TableCell>
                        <TableCell>{guest.email || 'N/A'}</TableCell>
                        <TableCell>
                          <Box display="flex" flexDirection="column" gap={0.5}>
                            {guest.invalidReasons?.map((reason: string, idx: number) => (
                              <Chip 
                                key={idx}
                                label={reason} 
                                size="small" 
                                color="error" 
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvalidGuestsDialogOpen(false)}>
            Close
          </Button>
          {invalidGuests.length > 0 && (
            <Button
              onClick={handleCleanupInvalidGuests}
              variant="contained"
              color="error"
              disabled={cleanupLoading}
              startIcon={cleanupLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
            >
              {cleanupLoading ? 'Deleting...' : `Delete ${invalidGuests.length} Invalid Guest${invalidGuests.length > 1 ? 's' : ''}`}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};